/**
 * Story archetype — ART-FIRST composition (TT-1046).
 *
 * The artwork IS the composition. The metaphor's anchor sits large and
 * central; its regions (base / slope / peak, roots / trunk / canopy, tip /
 * above / below) are the layout. The arc is a hand-drawn route across the
 * art; big ideas sit AT their beat on the route (adjacency is the
 * connection — no threads); object art carries content (the climber's flag
 * holds the decisions, two clouds hold a tension, the sun marks the
 * landing); themes pack the margins and the pale wash of the art, so the
 * scene stays dense without leaving the picture.
 *
 * Occupancy: the anchor's dense paint is a hard obstacle, its pale wash is
 * free-with-a-cost (findSpot `softArt`), so text prefers paper but is
 * allowed onto the watercolor — the art was painted light for exactly that.
 *
 * Spec shape (domain/scenegen/story.py): { archetype:'story', title,
 * metaphor, arc, big_ideas, tensions, decisions, themes, fonts, palette }.
 * Composition per metaphor: sceneTemplates.js.
 *
 * Structure: solveStory builds a shared state `S`, then runs the sections in
 * order — title, objects, route, beats (structural: fixed to the art), then
 * the content sections (ideas, decisions, tensions, themes) in TWO passes:
 * everything already on the page (a `prev` rect) first, new arrivals
 * second, so a newcomer in an earlier section can never take the spot a
 * held item in a later section is about to reclaim. Each section emits into
 * its own bucket and the buckets are concatenated in section order, so the
 * draw order does not depend on what is held.
 */

import { makeRng, seedFor, randRange } from '../lib/tapestryGenerative.js';
import { sceneArt } from '../lib/artPick.js';
import { pointInPolygon, polygonBBox, rectCenter, rectsOverlap } from './geometry.js';
import { bannerNotch, cloudReach, containerPath, containerPadding } from './hand.js';
import { TERRAIN_TINT, terrainFill, terrainTrim } from '../board/terrain.js';
import { NO_REACH, intentOf, isIntent, motifTrim } from '../board/motifs.js';
import { textBoxReach } from '../board/motifArt.js';
import { textBoxPaint } from '../board/render.js';
import { WALL, layoutCluster, clusterParts } from './wallSolver.js';
import { slug, iconSymbolId } from './sceneText.js';
import { LINE_GAP, fmt, up, wrap, line, blockSink, emitClusterItems, latestSent, paperPatch, placeTitle } from './sceneText.js';

const QUOTE_MAX_W = 330;
const THEME_MAX_W = 340;
// Themes are texture, but texture must still read from a seat: at this
// scale a fragment is 16 sheet units (~11 px on a 700 px-tall page), the
// board's floor. Nothing on a story board is set smaller (TT-1168 note 6:
// the compact rungs that went to 0.42 of the wall's registers are gone).
const THEME_SCALE = 0.85;
const QUOTE_FS = 20; // the story's script register (STORY.quote)
// The theme tree's fit rungs: wide, natural, then narrow — never smaller.
const THEME_RUNGS = [1.6, 1, 0.75].map((k) => ({ maxW: THEME_MAX_W * k, scale: THEME_SCALE, quoteFs: QUOTE_FS }));
// What a theme gives up when the wall is short, step by step: its third
// sub-head, a quote, another sub-head, then its last quote and the
// sub-heads again — never its size (a fragment that cannot be read is not
// texture; and a verbatim line outranks a sub-head — TT-1162 note 3). The
// first step is the whole tree; the others skip the widest rung.
const THEME_STEPS = [{}, { children: 2 }, { children: 2, quotes: 1 }, { children: 1, quotes: 1 }, { quotes: 0 }, { children: 2, quotes: 0 }, { children: 1, quotes: 0 }];
/** The cluster with only its first `children` sub-heads and `quotes` quotes. */
const trimCluster = (cl, { children = Infinity, quotes = Infinity } = {}) => ({ ...cl, children: (cl.children || []).slice(0, children), quotes: (cl.quotes || []).slice(0, quotes) });

export const STORY = {
  // Registers.
  titleWord: 90, // TT-1162: a little smaller (was 104)
  qualifier: 34,
  claim: 30,
  quote: QUOTE_FS,
  beat: 21,
  tension: 23,
  decisionHead: 26,
  decision: 21,
  lineGap: LINE_GAP,
  // Search: every block is first sought on sheet 1 within `radius`; sky
  // (zone-restricted) tries are shorter.
  pad: 14,
  radius: 900,
  skyRadius: 420,
  skyMinH: 140, // fallback sky strip height when the anchor gives no ridge
  // Title, top-left; the reserve includes the accent underline (drawn at
  // ty + fs·underline, ±6 wobble) plus breathing room below it.
  titleAt: [40, 30],
  titleMaxFrac: 0.3,
  titleReserve: { padL: 10, padR: 20, top: 24, capFrac: 0.95, below: 24 },
  titleUnderline: 0.95,
  // Objects (template art).
  objectPad: 10,
  // Route.
  trailStart: [160, 50], // the trail begins this far left / below the first beat
  trailHead: 16,
  arcLead: 140, // the open board's swoosh starts this far left of the first beat
  arcTail: 70, // and runs on past the last one to its arrowhead
  plumbTail: 24, // the sounding line hangs this far past the last beat
  routeHalf: 6, // reserveRoute: the line's half-width, plus a hand's wobble
  // Beats: dot radius (trail) / numbered mark radius, label box.
  beatDot: 7,
  beatMark: 15,
  beatMaxW: 210,
  beatPad: 6,
  beatOff: { trail: 16, depth: 44, marks: 12 }, // label offset from the dot (marks: past the mark radius)
  // A label belongs beside its mark (TT-1162 note 4, TT-1168 note 4):
  // sought within `beatRadius` of its seat at each width rung — beside the
  // mark on the side away from the idea, then the other side, then above
  // and below it — never anywhere on the sheet; a numbered mark whose label
  // found no room is not drawn at all. Past `beatLeader` from the mark, a
  // short line joins the two.
  beatLadder: [210, 150, 110],
  beatRadius: 60,
  beatLeader: 24,
  beatOnArt: 0.75, // a label tag brings its own paper: the dense paint it may sit on
  // Ideas: icon + banner claim, quote beneath. Natural width first, then a
  // narrower, taller layout (a pocket that is a hair too small is common);
  // only the last rung may grow the wall.
  ideaIcon: 70,
  ideaGap: 10,
  quoteMaxW: QUOTE_MAX_W,
  quoteGap: 6,
  ideaLadder: [
    { claimW: 340, quoteW: QUOTE_MAX_W },
    { claimW: 250, quoteW: 280 },
  ],
  fan: [0, -1, 1, -2, 2], // same-beat ideas fan around the beat
  ideaSideGap: 60, // from the beat to the idea's near edge (left / right)
  ideaLift: 20, // ideas sit a little above their beat
  ideaFanX: 40, // extra spacing between fanned ideas (above / below: sideways)
  ideaFanY: 30, // (left / right: vertically)
  ideaArtGap: 40, // 'above' / 'below' ideas hang this far off the art's edge
  // Decisions: the box must land — natural shape, wide/short, narrow/tall,
  // then a compact register.
  decisionLadder: [
    { scale: 1, maxW: 320 },
    { scale: 1, maxW: 460 },
    { scale: 1, maxW: 240 },
    { scale: 0.85, maxW: 320 },
    { scale: 0.85, maxW: 460 },
    { scale: 0.85, maxW: 240 },
  ],
  decisionHeadW: 300,
  decisionCheck: 24,
  decisionHeadGap: 6,
  decisionGap: 5,
  decisionCarrierGap: 16, // beside the carrier
  decisionBelowGap: 12, // under a flying carrier
  decisionPeakDx: 220, // fallback: right of the landing point
  // Tensions: two clouds pulling apart.
  tensionMaxW: 170,
  tensionGap: 60, // between the clouds, side by side
  tensionGapStacked: 44, // one above the other
  tensionPad: 12,
  // Shape ladder for a tension the sky cannot take side by side (see
  // placeLeftoverTensions): stacked, then a compact stacked register.
  tensionLadder: [
    { stacked: false, scale: 1 },
    { stacked: true, scale: 1 },
    { stacked: true, scale: 0.85 },
    { stacked: false, scale: 0.7 },
    { stacked: true, scale: 0.7 },
  ],
  tensionPadLeftover: 8, // a deferred tension may sit closer to its neighbours
  tensionRowGap: 30,
  tensionArrowInset: 10,
  tensionHead: 7,
  // Themes: texture in the template's slots. Wide (children in a row —
  // fits the strips under the art) → narrow → small. Texture never earns a
  // second sheet: a theme that still doesn't fit is left off (it stays in
  // debug.skipped) — a recorder runs out of wall.
  themeScale: THEME_SCALE,
  themeMaxW: THEME_MAX_W,
  // Every width rung is tried at each give-up step (THEME_STEPS) before
  // the theme itself is dropped (TT-1168 note 6).
  themeLadder: THEME_STEPS.flatMap((keep, i) => (i ? THEME_RUNGS.slice(1) : THEME_RUNGS).map((r) => ({ ...r, keep }))),
  themeSlots: ['left-low', 'right-mid'], // when the template names none
  themeSlotTries: 6,
  themeJitter: 25,
  themePad: 10,
  themeArrowStroke: 2.2, // lighter than the wall's
  themeArrowHead: 6,
};

const STAGES = ['opening', 'discovery', 'turn', 'landing'];
const CONTENT_SECTIONS = ['ideas', 'decisions', 'tensions', 'themes'];

const byStage = (list) => (list || []).slice().sort((a, b) => STAGES.indexOf(a.stage) - STAGES.indexOf(b.stage));

/** An open arrowhead (two barbs) at `tip`, pointing along `ang`. */
function arrowHead([x, y], ang, size) {
  const barb = (k) => `${fmt(x - size * Math.cos(ang + k))} ${fmt(y - size * Math.sin(ang + k))}`;
  return `M ${barb(-0.5)} L ${fmt(x)} ${fmt(y)} L ${barb(0.5)}`;
}

/** A cubic Bézier as a polyline of `n` segments. */
function cubicPoints(p0, c1, c2, p1, n = 16) {
  return Array.from({ length: n + 1 }, (_, k) => {
    const t = k / n;
    const u = 1 - t;
    const a = u * u * u;
    const b = 3 * u * u * t;
    const c = 3 * u * t * t;
    const d = t * t * t;
    return [a * p0[0] + b * c1[0] + c * c2[0] + d * p1[0], a * p0[1] + b * c1[1] + c * c2[1] + d * p1[1]];
  });
}

/**
 * The route is drawn content: once the beats are labelled, its polylines go
 * on `ctx.route`, so ideas, decisions, tensions and themes keep off the line
 * instead of being lettered across it (TT-1229: the wave's trail ran through
 * the base-left theme). Beat labels come first — they sit at the line by
 * design. Crossing it is findSpot's last resort, not a block (a dropped
 * theme is worse), and a held block is never moved by a later segment.
 */
function reserveRoute(S, ctx) {
  ctx.route = { lines: S.routeLines, half: STORY.routeHalf };
}

/** Hand-wobbly polyline through points. */
function handPath(pts, rng, wobble = 6) {
  if (pts.length < 2) return '';
  const j = () => randRange(rng, -wobble, wobble);
  let d = `M ${fmt(pts[0][0])} ${fmt(pts[0][1])}`;
  for (let i = 1; i < pts.length; i++) {
    const [x0, y0] = pts[i - 1];
    const [x1, y1] = pts[i];
    d += ` Q ${fmt((x0 + x1) / 2 + j() * 3)} ${fmt((y0 + y1) / 2 + j() * 3)} ${fmt(x1 + j() * 0.3)} ${fmt(y1 + j() * 0.3)}`;
  }
  return d;
}

/**
 * An emission bucket: one per section, concatenated in section order.
 * Everything is drawn through a block (`E.block(id)`: text / shape / icon
 * sinks stamped with the block, see sceneText.blockSink), so the reveal
 * schedule can pace each block as one thing.
 */
function bucket() {
  const E = { texts: [], shapes: [], icons: [], images: [] };
  E.block = (id) => blockSink(E, id);
  return E;
}

// ------------------------------------------------------------------ state

/**
 * Shared solve state. Beyond the inputs it carries the points on the art
 * (stage → point), the sky strip, and `place`, the story's placement policy:
 * sheet 1 first (soft art), widen to the wall only when it already has more
 * than one sheet, and grow the wall only when asked (`extend`).
 */
function makeState(spec, ctx, { measure, placeBlock, commitSpot, holdLadder, palette, fonts, prev, T, textBoxArt }) {
  const { sheet, anchor, assets, margin: M } = ctx;
  const S = { spec, ctx, T, sheet, anchor, assets, M, prev, palette, fonts, measure, commitSpot, holdLadder, textBoxArt };
  // Every caps register is uppercased by its caller before it is measured.
  S.capsM = (t, fs) => measure(t, fs, fonts.caps);
  S.scriptM = (t, fs) => measure(t, fs, fonts.script);
  S.rng = makeRng(seedFor(spec.id || 'story', 23));

  // Sheet 1 is the board; the wall only grows when the board is truly full.
  S.sheet1 = [
    [M, M],
    [sheet.w - M, M],
    [sheet.w - M, sheet.h - M],
    [M, sheet.h - M],
  ];
  S.place = (id, w, h, target, { pad = STORY.pad, parts = null, extend = false, softArt = true, zone = null, radius = STORY.radius, holdOnly = false } = {}) =>
    placeBlock(ctx, prev, { id, w, h, target, zone: zone || S.sheet1, parts, pad, softArt, radius, holdOnly, widen: !zone, extend: !zone && extend });
  // The first of `targets` that places. A held block is judged on the
  // first alone: a hold is about the old rect, not the seat.
  S.placeAt = (id, w, h, targets, opts = {}) => {
    for (const tg of targets) {
      const r = S.place(id, w, h, tg, opts);
      if (r || opts.holdOnly) return r;
    }
    return null;
  };

  // ---- Points on the art.
  const regionPt = (name, fx = 0.5, fy = 0.5) => {
    if (name === 'sheet') return [sheet.w * fx, sheet.h * fy];
    const poly = anchor?.regions?.[name];
    if (!poly) return null;
    const b = polygonBBox(poly);
    return [b.x + b.w * fx, b.y + b.h * fy];
  };
  const fallbackPoint = (stage) => {
    const k = STAGES.indexOf(stage) / (STAGES.length - 1);
    return [sheet.w * (0.3 + 0.4 * k), sheet.h * (0.85 - 0.6 * k)];
  };
  S.stagePoint = (stage) => {
    const [name, fx, fy] = T.stages[stage] || T.stages.discovery;
    return regionPt(name, fx, fy) || fallbackPoint(stage);
  };
  S.beats = (spec.arc || []).filter((b) => b && b.stage);
  // Beats are keyed by STAGE, not position: `specAtSentence` drops unheard
  // beats, so a positional id would re-key (and re-seed) every beat behind it.
  // A repeated stage gets a suffix.
  S.beatIds = [];
  S.beats.forEach((b) => {
    const base = `beat-${b.stage}`;
    const n = S.beatIds.filter((id) => id === base || id.startsWith(`${base}-`)).length;
    S.beatIds.push(n ? `${base}-${n + 1}` : base);
  });
  S.beatPts = S.beats.map((b) => S.stagePoint(b.stage));
  // Ideas and objects anchor to the stage's own point on the art, even when
  // a route (depth) later pulls the beat marks onto one line.
  const stagePts = S.beatPts.map((p) => [...p]);
  S.pointFor = (stage) => {
    const i = S.beats.findIndex((b) => b.stage === stage);
    return i >= 0 ? stagePts[i] : S.stagePoint(stage);
  };
  S.art = anchor?.painted || { x: sheet.w * 0.15, y: sheet.h * 0.2, w: sheet.w * 0.7, h: sheet.h * 0.8 };

  // The sky strip above the art — where clouds, and the decisions box when
  // its carrier flies, belong. Zone-restricted tries here never wander down
  // onto the paint. (sceneSolver derives zones.sky from the anchor's ridge
  // profile — the paint's actual top per column — not from the image box,
  // which for band art like the river reaches the top edge with faint wash.)
  const skyH = Math.max(M + STORY.skyMinH, S.art.y - 10);
  S.skyZone = ctx.zones?.sky || [[M, M], [sheet.w - M, M], [sheet.w - M, skyH], [M, skyH]];
  const skyBox = polygonBBox(S.skyZone);
  S.skyBox = skyBox;
  S.skyBottom = skyBox.y + skyBox.h;
  S.placeInSky = (id, w, h, targets, pad) => {
    if (h + 2 * pad > skyBox.h) return null;
    const seats = targets.map((tg) => [tg[0], Math.min(tg[1], S.skyBottom - h / 2 - pad)]);
    return S.placeAt(id, w, h, seats, { pad, zone: S.skyZone, radius: STORY.skyRadius });
  };
  S.objects = {};

  // ---- Terrain: what a container sits in, per the template (sceneTemplates
  // `terrain`). The sky test uses the rect's centre (a banner beside the
  // slope is in the air); the art tests use its foot (what it sits on). The
  // sky ZONE follows the ridge, which ignores the pale foothill wash and so
  // reaches the page bottom beside a low flank — the horizon caps it.
  const TT = T.terrain || {};
  const inRegion = (p, name) => Boolean(anchor?.regions?.[name] && pointInPolygon(p, anchor.regions[name]));
  S.horizonY =
    TT.horizon == null ? null
    : typeof TT.horizon === 'number' ? S.art.y + S.art.h * TT.horizon
    : anchor?.regions?.[TT.horizon] ? polygonBBox(anchor.regions[TT.horizon]).y
    : null;
  S.terrainAt = (r) => {
    const foot = [r.x + r.w / 2, r.y + r.h];
    for (const [name, terrain] of Object.entries(TT.regions || {})) if (inRegion(foot, name)) return terrain;
    if (S.horizonY == null) return 'neutral';
    const mid = rectCenter(r);
    if (mid[1] < S.horizonY && ctx.zones?.sky && pointInPolygon(mid, ctx.zones.sky)) return 'sky';
    if (foot[1] >= S.horizonY || (anchor && rectsOverlap(r, anchor.painted))) return TT.below || 'ground';
    return 'neutral';
  };
  // A story dresses every dressable container (a handful; a miss reads as a
  // slip). Records the answer for the lab's debug overlay.
  ctx.terrainOf = {};
  // A box's dress: the intent the plan names wins over the terrain it hangs
  // in (the board's textBoxDress, TT-1269 / TT-1330).
  S.dressing = (id, r, intent = null) => {
    const terrain = S.terrainAt(r);
    const dress = intent || (TERRAIN_TINT[terrain] ? terrain : null);
    ctx.terrainOf[id] = { terrain, dress };
    return dress;
  };
  // Trim strokes as shapes, after the container so they may cross its edge:
  // a terrain's grass and ripples, a motif's rays, roll or gutter.
  S.emitTrim = (E, id, dress, r, sent) => {
    const B = E.block(id); // trim belongs to the block it dresses
    if (!dress || textBoxArt[dress]) return; // a painted piece carries its own rays and curls
    (isIntent(dress) ? motifTrim : terrainTrim)(dress, r, makeRng(seedFor(id, 37))).forEach((t, k) => {
      B.shape({ ...t, id: `${id}-trim-${k}`, kind: 'trim', fillOpacity: 1, sent, z: 3 });
    });
  };
  // The text box of a claim with an intent (TT-1330), inked as the board inks it
  // (render.js textBoxPaint): the painted piece when the host has the art
  // (`textBoxArt`, the text-box manifest) — the canvas draws a `piece`
  // shape as the piece's tiles — else the drawn motif's ink line (a plain
  // one for a painted-only intent, TT-1333).
  S.emitIntent = (E, id, intent, r, sent) => {
    const B = E.block(id);
    const p = textBoxPaint(intent, r, seedFor(id, 41), palette, textBoxArt);
    if (p.piece) B.shape({ id: `${id}-piece`, kind: 'piece', piece: p.piece, rect: r, sent, z: 2 });
    // a width of 0 (the burst has no ring) must read as no stroke: the canvas defaults a falsy width to 3
    else B.shape({ id: `${id}-banner`, kind: 'container', d: p.ink.d, stroke: p.ink['stroke-width'] ? p.ink.stroke : 'none', fill: p.ink.fill, fillOpacity: 0.95, strokeWidth: p.ink['stroke-width'], sent, z: 2 });
  };
  return S;
}

// --------------------------------------------------------------- sections

/**
 * Title in the sky, top-left. Committed before anything is placed: the
 * title's corner is fixed, so nothing else may take it.
 */
function placeTitleBlock(S, E) {
  const { spec, ctx, sheet, palette, fonts, rng } = S;
  // A template may centre a bigger title (the open board); else top-left.
  const look = S.T.title || {};
  const t = placeTitle(spec, ctx, {
    measure: S.capsM,
    E,
    commitSpot: S.commitSpot,
    fonts,
    palette,
    sizes: { word: look.word || STORY.titleWord, qualifier: STORY.qualifier },
    maxFrac: look.maxFrac || STORY.titleMaxFrac,
    center: !!look.center,
    x: look.center ? sheet.w / 2 : STORY.titleAt[0],
    y: STORY.titleAt[1],
    reserve: STORY.titleReserve,
  });
  const ul = { x: t.x, y: t.ty + t.titleFs * STORY.titleUnderline, w: t.wordW, h: 6 };
  E.block('title').shape({ id: 'title-ul', kind: 'connector', d: containerPath('underline', ul, rng), stroke: palette.accent, fill: 'none', strokeWidth: 4, z: 2 });
}

/** Object art (template): placed before content so content can sit beside it. */
function placeObjects(S, E) {
  const { T, sheet, assets } = S;
  for (const o of T.objects || []) {
    const asset = sceneArt(assets, o.symbol_id, 'mid');
    if (!asset) continue;
    const [bx0, by0, bx1, by1] = asset.bbox;
    const paintedAspect = ((bx1 - bx0) * asset.width) / ((by1 - by0) * asset.height);
    const h = sheet.h * o.size;
    const w = h * paintedAspect;
    let target;
    if (o.at) target = [o.at[0] * sheet.w, o.at[1] * sheet.h];
    else {
      const p = S.pointFor(o.near);
      target = [p[0] + (o.offset?.[0] || 0), p[1] + (o.offset?.[1] || 0)];
    }
    const r = S.place(o.id, w, h, target, { pad: STORY.objectPad, softArt: true });
    if (!r) continue;
    const fw = w / (bx1 - bx0);
    const fh = h / (by1 - by0);
    E.images.push({ id: o.id, url: asset.url, reveal: asset.reveal, x: r.x - bx0 * fw, y: r.y - by0 * fh, w: fw, h: fh, z: 1, opacity: 1 });
    S.objects[o.id] = { ...o, rect: r };
  }
}

/**
 * The route: how the beats connect is the metaphor's business.
 *   trail — a walked path with waypoints (mountain / journey)
 *   marks — no line; the art itself is the arc, beats are numbered marks
 *           on it (a tree: roots → trunk → canopy)
 *   depth — a sounding line: dashed plumb line + horizontal ticks down the
 *           art, beats numbered by depth (an iceberg); the beat points are
 *           moved onto the line.
 *   arc   — no art: one smooth hand-drawn swoosh through the beats with an
 *           arrowhead, beats numbered marks on it (the open board).
 */
function drawRoute(S, E) {
  const { T, sheet, M, palette, rng, beats, beatPts, beatIds } = S;
  const route = T.route || 'trail';
  S.route = route;
  // The line as drawn (polylines, sheet units), for reserveRoute.
  S.routeLines = [];
  if (beatPts.length && route === 'trail') {
    const [sx, sy] = STORY.trailStart;
    const start = [Math.max(M + 10, beatPts[0][0] - sx), Math.min(sheet.h - 50, beatPts[0][1] + sy)];
    const pts = [start, ...beatPts];
    S.routeLines.push(pts);
    for (let i = 1; i < pts.length; i++) {
      // Segment keyed by from→to stage (see beatIds).
      const pid = `path-${i > 1 ? beats[i - 2].stage : 'start'}-${beats[i - 1].stage}`;
      // A segment arrives with the beat it leads from.
      E.block(beatIds[i - 1]).shape({ id: pid, kind: 'connector', d: handPath([pts[i - 1], pts[i]], makeRng(seedFor(pid, 7)), 5), stroke: palette.accent, fill: 'none', strokeWidth: 5, sent: beats[i - 1]?.sent, z: 1 });
    }
    const [ex, ey] = pts[pts.length - 1];
    const [px0, py0] = pts[pts.length - 2];
    const ang = Math.atan2(ey - py0, ex - px0);
    E.block(beatIds[beats.length - 1]).shape({ id: 'path-head', kind: 'connector', d: arrowHead([ex, ey], ang, STORY.trailHead), stroke: palette.accent, fill: 'none', strokeWidth: 5, sent: beats[beats.length - 1]?.sent, z: 1 });
  }
  if (beatPts.length && route === 'arc') {
    // Lead in from the left edge, then one cubic per beat (a segment
    // arrives with the beat it leads to). Each segment is built from the
    // beats behind it only — its first control mirrors the previous
    // segment's last, so the swoosh stays smooth — and its wobble is seeded
    // by its own id: a live board's next beat never reshapes inked arc.
    const [x0, y0] = beatPts[0];
    const pts = [[Math.max(M + 10, x0 - STORY.arcLead), y0 + STORY.arcLead * 0.25], ...beatPts];
    let prevC2 = null;
    for (let i = 1; i < pts.length; i++) {
      const [p1, p2] = [pts[i - 1], pts[i]];
      const pid = `arc-${i > 1 ? beats[i - 2].stage : 'start'}-${beats[i - 1].stage}`;
      const sr = makeRng(seedFor(pid, 7));
      const j = () => randRange(sr, -6, 6);
      const c1 = prevC2 ? [2 * p1[0] - prevC2[0], 2 * p1[1] - prevC2[1]] : [p1[0] + (p2[0] - p1[0]) / 3, p1[1] + (p2[1] - p1[1]) / 3];
      const c2 = [p2[0] - (p2[0] - p1[0]) / 3 + j(), p2[1] - (p2[1] - p1[1]) / 3 + j()];
      prevC2 = c2;
      S.routeLines.push(cubicPoints(p1, c1, c2, p2));
      E.block(beatIds[i - 1]).shape({ id: pid, kind: 'connector', d: `M ${fmt(p1[0])} ${fmt(p1[1])} C ${fmt(c1[0])} ${fmt(c1[1])} ${fmt(c2[0])} ${fmt(c2[1])} ${fmt(p2[0])} ${fmt(p2[1])}`, stroke: palette.accent, fill: 'none', strokeWidth: 7, sent: beats[i - 1]?.sent, z: 1 });
    }
    // Runs on past the last beat, along the curve's last tangent, to its head.
    const [ex, ey] = pts[pts.length - 1];
    const ang = Math.atan2(ey - prevC2[1], ex - prevC2[0]);
    const tip = [ex + STORY.arcTail * Math.cos(ang), ey + STORY.arcTail * Math.sin(ang)];
    S.routeLines.push([[ex, ey], tip]);
    E.block(beatIds[beats.length - 1]).shape({ id: 'arc-head', kind: 'connector', d: `M ${fmt(ex)} ${fmt(ey)} L ${fmt(tip[0])} ${fmt(tip[1])} ${arrowHead(tip, ang, STORY.trailHead * 1.4)}`, stroke: palette.accent, fill: 'none', strokeWidth: 7, sent: beats[beats.length - 1]?.sent, z: 1 });
  }
  if (beatPts.length > 1 && route === 'depth') {
    // Plumb line hangs from the first beat straight down past the last.
    const px = beatPts[0][0];
    const y0 = beatPts[0][1];
    const y1 = beatPts[beatPts.length - 1][1] + STORY.plumbTail;
    S.routeLines.push([[px, y0], [px, y1]]);
    E.block(beatIds[0]).shape({ id: 'plumb', kind: 'connector', d: `M ${fmt(px)} ${fmt(y0)} L ${fmt(px + randRange(rng, -4, 4))} ${fmt(y1)}`, stroke: palette.accent, fill: 'none', strokeWidth: 3, strokeDasharray: '10 9', sent: beats[0]?.sent, z: 1 });
    beats.forEach((b, i) => {
      const [, y] = beatPts[i];
      const side = (T.ideaSide[b.stage] || 'left') === 'left' ? 1 : -1; // tick toward the label
      E.block(beatIds[i]).shape({ id: `tick-${b.stage}`, kind: 'connector', d: `M ${fmt(px - 14 * side)} ${fmt(y)} L ${fmt(px + 34 * side)} ${fmt(y + randRange(rng, -2, 2))}`, stroke: palette.accent, fill: 'none', strokeWidth: 4, sent: b.sent, z: 1 });
      beatPts[i] = [px, y]; // beats sit on the plumb line
    });
  }
}

/**
 * Beat = a signpost: a waypoint dot (trail) or a numbered mark (marks /
 * depth) on the art, label just off it, on the side opposite the idea.
 */
function placeBeats(S, E) {
  const { T, palette, fonts, beats, beatPts, beatIds, route, ctx } = S;
  const r = route === 'trail' ? STORY.beatDot : STORY.beatMark;
  // Every dot (and its number) is an obstacle like anything else drawn —
  // all of them before any label, so an early beat's label can never take
  // the spot a later beat's mark is about to occupy (TT-1229: the mandala's
  // ring labels ran over the centre mark).
  beatPts.forEach(([x, y]) => S.commitSpot(ctx, { x: x - r, y: y - r, w: r * 2, h: r * 2 }));
  beats.forEach((b, i) => {
    const [x, y] = beatPts[i];
    const bid = beatIds[i];
    const B = E.block(bid);
    const circle = `M ${fmt(x - r)} ${fmt(y)} a ${r} ${r} 0 1 0 ${r * 2} 0 a ${r} ${r} 0 1 0 ${-r * 2} 0`;
    // The label sits beside its mark, on the side away from the idea, or
    // where the template names (`labelSide`: the mandala's ring marks label
    // above, leaving its small centre to the last beat — TT-1229). Each
    // width rung is tried close to the mark and nowhere else (a beat's
    // label two ideas away labels nothing).
    const ideaS = T.ideaSide[b.stage] || 'left';
    const named = T.labelSide?.[b.stage];
    const side = named || (ideaS === 'left' ? 'right' : ideaS === 'right' ? 'left' : i % 2 ? 'left' : 'right');
    const off = route === 'trail' || route === 'depth' ? STORY.beatOff[route] : STORY.beatMark + STORY.beatOff.marks;
    // Held first, like every other register: the old spot absorbs whichever
    // rung it can before the label moves.
    const layouts = STORY.beatLadder.map((maxW) => {
      const lay = wrap(up(b.label), STORY.beat, maxW, S.capsM);
      return { ...lay, w: lay.w + STORY.beatPad * 2, h: lay.h + 4 };
    });
    // Seats, nearest first: beside the mark away from the idea, beside it
    // on the idea's side, above it, below it.
    const seats = (L) => {
      const dx = (side === 'left' ? -1 : 1) * (off + L.w / 2);
      const dy = r + STORY.beatPad + L.h / 2 + 4;
      const beside = [[x + dx, y - 4], [x - dx, y - 4]];
      const over = [[x, y - dy], [x, y + dy]];
      return side === 'above' ? [...over, ...beside] : [...beside, ...over];
    };
    const tryAt = (L, holdOnly) => S.placeAt(bid, L.w, L.h, seats(L), { pad: STORY.beatPad, softArt: STORY.beatOnArt, radius: STORY.beatRadius, holdOnly });
    let { r: rect, L: lay } = S.holdLadder(S.prev?.[bid], layouts, tryAt) || {};
    for (const L of layouts) {
      if (rect) break;
      lay = L;
      rect = tryAt(L, false);
    }
    // A numbered mark with no label beside it would point at nothing.
    if (!rect && route !== 'trail') return;
    if (route === 'trail') {
      B.shape({ id: `${bid}-dot`, kind: 'connector', d: circle, stroke: palette.ink, fill: palette.paper, strokeWidth: 3, sent: b.sent, z: 3 });
    } else {
      B.shape({ id: `${bid}-dot`, kind: 'connector', d: circle, stroke: palette.accent, fill: palette.paper, fillOpacity: 0.95, strokeWidth: 3.5, sent: b.sent, z: 3 });
      const num = String(i + 1);
      const numFs = r * 1.5;
      B.text(`${bid}-num`, line(num, numFs, S.capsM), x, y - numFs * 0.62, numFs, fonts.caps, palette.accent, { sent: b.sent, uppercase: true, center: true });
    }
    if (!rect) return;
    // A label that had to stand off gets a leader from the mark's rim to
    // its near edge, so the number and its text read as one.
    const nearX = rect.x + rect.w / 2 < x ? rect.x + rect.w : rect.x;
    const gap = Math.abs(nearX - x) - r;
    if (gap > STORY.beatLeader) {
      const dir = nearX < x ? -1 : 1;
      const ly = rect.y + rect.h / 2;
      B.shape({ id: `${bid}-lead`, kind: 'connector', d: `M ${fmt(x + dir * r)} ${fmt(y)} L ${fmt(nearX - dir * 2)} ${fmt(ly)}`, stroke: palette.accent, fill: 'none', strokeWidth: 2.5, sent: b.sent, z: 2 });
    }
    paperPatch(B, `${bid}-bg`, rect, palette.paper, { opacity: 0.9, sent: b.sent, z: 2, rng: makeRng(seedFor(bid, 9)) });
    B.text(bid, lay, rect.x + STORY.beatPad, rect.y + 2, STORY.beat, fonts.caps, palette.ink, { sent: b.sent, uppercase: true });
  });
}

/**
 * Lay out one idea (icon + claim, quote beneath) at a rung: a claim / quote
 * width and, for a idea with an intent (TT-1330), the `intent` whose painted text box
 * the claim sits in instead of the banner — a plain rect the motif is drawn
 * round, with the paint's reach outside it reserved so neighbours stay
 * clear. `box` is the claim's box, reach included; `icon` the icon's, or
 * null for an idea without one (its symbol drawn elsewhere in the scene):
 * the claim then takes the icon's width too, and centres over its quote.
 */
function layoutIdea(S, idea, { claimW, quoteW, intent = null }) {
  const hasIcon = !!iconSymbolId(idea);
  const iconW = (S.T.ideaIcon || STORY.ideaIcon) + STORY.ideaGap; // the icon and its gap
  const iconS = hasIcon ? iconW - STORY.ideaGap : 0;
  const claim = wrap(up(idea.claim), STORY.claim, hasIcon ? claimW : claimW + iconW, S.capsM);
  const pad = containerPadding('banner', STORY.claim);
  const bannerH = claim.h + pad * 1.2;
  const notch = intent ? 0 : bannerNotch(bannerH); // the notched ends cut into the rect
  const bannerW = claim.w + (pad + notch) * 2 + 6;
  const reach = intent ? textBoxReach(intent, { w: bannerW, h: bannerH }, S.textBoxArt) : NO_REACH;
  const boxW = bannerW + reach.left + reach.right;
  const boxH = bannerH + reach.top + reach.bottom;
  const quote = idea.quote ? wrap(`“${idea.quote}”`, STORY.quote, quoteW, S.scriptM) : null;
  const topH = Math.max(boxH, iconS);
  const w = Math.max(hasIcon ? iconW + boxW : boxW, quote ? quote.w + iconS * 0.3 : 0);
  const h = topH + (quote ? STORY.quoteGap + quote.h : 0);
  const icon = hasIcon ? { x: 0, y: (topH - iconS) / 2, w: iconS, h: iconS } : null;
  const box = { x: hasIcon ? iconW : (w - boxW) / 2, y: (topH - boxH) / 2, w: boxW, h: boxH };
  const quoteX = quote && (hasIcon ? iconS * 0.3 : (w - quote.w) / 2);
  const parts = [...(icon ? [icon] : []), box, ...(quote ? [{ x: quoteX, y: topH + STORY.quoteGap, w: quote.w, h: quote.h }] : [])];
  return { claim, pad, bannerH, notch, bannerW, icon, box, quoteX, quote, topH, w, h, parts, intent, reach };
}

/**
 * Big ideas AT their beat: banner claim + icon, quote beneath. Ids are
 * guaranteed by solveScene (withStringIds, from the SPEC index — before any
 * sort). The fan slot around a shared beat is fixed from the stage order,
 * not the placement order, so a held neighbour does not shift a newcomer.
 */
function placeIdeas(S, E, items) {
  const { T, sheet, M, art, anchor, palette, fonts } = S;
  items.forEach((idea) => {
    const { id } = idea;
    const pt = S.pointFor(idea.stage);
    const side = T.ideaSide[idea.stage] || 'left';
    const fan = S.fanOf[id];
    const vertical = side === 'above' || side === 'below';
    // 'above' / 'below': the idea hangs over (or under) the ART's edge (not
    // the beat — "above a deep beat" would be on the paint); same-beat ideas
    // then fan sideways instead of vertically. The target is clamped to sheet 1.
    const targetFor = (w, h) => {
      const tx0 = vertical ? pt[0] + fan * (w + STORY.ideaFanX) : pt[0] + (side === 'left' ? -(w / 2 + STORY.ideaSideGap) : w / 2 + STORY.ideaSideGap);
      // No art (the open board): above / below the beat itself.
      const top = anchor ? art.y : pt[1];
      const bottom = anchor ? art.y + art.h : pt[1];
      const ty0 = vertical ? (side === 'above' ? top - (h / 2 + STORY.ideaArtGap) : bottom + h / 2 + STORY.ideaArtGap) : pt[1] - STORY.ideaLift + fan * (h + STORY.ideaFanY);
      return [Math.max(M + w / 2, Math.min(sheet.w - M - w / 2, tx0)), Math.max(M + h / 2, Math.min(sheet.h - M - h / 2, ty0))];
    };
    // A idea with an intent tries its painted text box down the ladder, then the
    // same rungs as a plain banner (fit-or-plain, as the board's packer):
    // the decoration never costs the idea its place.
    const named = intentOf(idea);
    const intent = isIntent(named) ? named : null;
    const rungs = STORY.ideaLadder.map((rung) => ({ ...rung, intent })).concat(intent ? STORY.ideaLadder : []);
    const layouts = rungs.map((rung) => layoutIdea(S, idea, rung));
    const tryAt = (L, holdOnly, extend = false) => S.place(id, L.w, L.h, targetFor(L.w, L.h), { parts: L.parts, holdOnly, extend });
    // Held: can the old spot absorb any layout? Only then may the idea move.
    let { r, L } = S.holdLadder(S.prev?.[id], layouts, tryAt) || {};
    layouts.forEach((lay, ai) => {
      if (r) return;
      L = lay;
      r = tryAt(L, false, ai === layouts.length - 1);
    });
    if (!r) return;
    const { claim, pad, bannerH, notch, bannerW, icon, box, quoteX, quote, topH, reach } = L;
    const rr = makeRng(seedFor(id, 13));
    const B = E.block(id);
    if (icon) B.icon({ id: `${id}-icon`, symbolId: iconSymbolId(idea), x: r.x + icon.x, y: r.y + icon.y, w: icon.w, h: icon.h, color: palette.accent, sent: idea.sent });
    // the claim's text rect: inside its part, past the paint's reach (none for a banner)
    const bx = r.x + box.x + reach.left;
    const by = r.y + box.y + reach.top;
    // The banner keeps its shape (it is the idea's register) but takes the
    // wash and trim of the terrain it hangs in: grass under one on the slope,
    // a puff beside one in the sky. A claim with an intent takes its painted text
    // box instead (TT-1330): the intent is its dress, and its trim.
    const banner = { x: bx, y: by, w: bannerW, h: bannerH };
    const dress = S.dressing(id, banner, L.intent);
    if (L.intent) S.emitIntent(E, id, L.intent, banner, idea.sent);
    else B.shape({ id: `${id}-banner`, kind: 'container', d: containerPath('banner', banner, rr), stroke: palette.ink, fill: terrainFill(dress, palette.tint), fillOpacity: 0.95, strokeWidth: 3, sent: idea.sent, z: 2 });
    B.text(`${id}-claim`, claim, bx + pad + notch + 3, by + pad * 0.5, STORY.claim, fonts.caps, palette.ink, { sent: idea.sent, uppercase: true });
    if (quote) {
      // Paper behind the quote so it reads on the wash. The quote is the
      // sentence as spoken (story.py drops an idea whose quote is not).
      const qr = { x: r.x + quoteX - 4, y: r.y + topH + 4, w: quote.w + 8, h: quote.h + 4 };
      paperPatch(B, `${id}-qbg`, qr, palette.paper, { opacity: 0.75, sent: idea.sent, rng: rr });
      B.text(`${id}-quote`, quote, r.x + quoteX, r.y + topH + STORY.quoteGap, STORY.quote, fonts.script, palette.ink, { sent: idea.sent, verbatim: true, tilt: randRange(rr, -0.008, 0.008) });
    }
    // Trim last: shapes paint in push order, and the quote's paper would
    // otherwise cover the ripples / grass under the banner.
    S.emitTrim(E, id, dress, banner, idea.sent);
  });
}

/** Lay out the decisions box (head + checked items) at a register scale / width. */
function layoutDecisions(S, decisions, { scale, maxW }) {
  const headFs = STORY.decisionHead * scale;
  const fs = STORY.decision * scale;
  const head = wrap('DECISIONS', headFs, STORY.decisionHeadW, S.capsM);
  const items = decisions.map((d) => ({ ...d, lay: wrap(up(d.text), fs, maxW, S.capsM) }));
  const pad = containerPadding('box', fs);
  const check = STORY.decisionCheck * scale;
  const innerW = Math.max(head.w, ...items.map((it) => it.lay.w + check));
  const innerH = head.h + STORY.decisionHeadGap + items.reduce((acc, it) => acc + it.lay.h + STORY.decisionGap, 0);
  return { headFs, fs, head, items, pad, check, w: innerW + pad * 2, h: innerH + pad * 2 };
}

/**
 * Decisions: carried by the template's object (the flag at the summit, or
 * a flying carrier — bird / sun / cloud — in which case the box lives in
 * the sky). The box is what the meeting decided — it must land.
 */
function placeDecisions(S, E, decisions) {
  if (!decisions.length) return;
  const { sheet, palette, fonts } = S;
  const id = 'decisions';
  const carrier = Object.values(S.objects).find((o) => o.role === 'decisions');
  const peak = S.pointFor('landing');
  const layouts = STORY.decisionLadder.map((rung) => layoutDecisions(S, decisions, rung));
  // Held: the old spot absorbs whichever layout it can before the box moves.
  let { r, L } = S.holdLadder(S.prev?.[id], layouts, (lay, holdOnly) => S.place(id, lay.w, lay.h, peak, { holdOnly })) || {};
  for (const lay of layouts) {
    if (r) break;
    L = lay;
    const { w, h } = L;
    const targets = [];
    if (carrier) {
      const cr = carrier.rect;
      const g = STORY.decisionCarrierGap;
      if (carrier.side === 'right') targets.push([cr.x + cr.w + g + w / 2, cr.y + cr.h * 0.35]);
      else if (carrier.side === 'below') targets.push([cr.x + cr.w / 2, cr.y + cr.h + STORY.decisionBelowGap + h / 2]);
      else targets.push([cr.x - g - w / 2, cr.y + cr.h * 0.35]);
    }
    if (carrier && carrier.rect.y + carrier.rect.h / 2 < S.skyBottom) r = S.placeInSky(id, w, h, [targets[0], [sheet.w * 0.75, S.skyBottom / 2], [sheet.w * 0.5, S.skyBottom / 2]], STORY.pad);
    if (r) break;
    targets.push([peak[0] + STORY.decisionPeakDx + w / 2, Math.max(80 + h / 2, peak[1] - 60)], [sheet.w * 0.8, sheet.h * 0.5], [sheet.w * 0.2, sheet.h * 0.85]);
    r = S.placeAt(id, w, h, targets);
  }
  if (!r) return;
  const { head, items, pad, check } = L;
  const rr = makeRng(seedFor(id, 29));
  const B = E.block(id);
  const firstSent = Math.min(...decisions.map((d) => d.sent ?? 0));
  const dress = S.dressing(id, r);
  B.shape({ id: `${id}-box`, kind: 'container', d: containerPath('box', r, rr), stroke: palette.ink, fill: terrainFill(dress, palette.paper), fillOpacity: 0.92, strokeWidth: 3, sent: firstSent, z: 2 });
  S.emitTrim(E, id, dress, r, firstSent);
  let y = r.y + pad;
  B.text(`${id}-head`, head, r.x + pad, y, L.headFs, fonts.caps, palette.accent, { sent: firstSent, uppercase: true });
  y += head.h + STORY.decisionHeadGap;
  items.forEach((it, i) => {
    const cx = r.x + pad;
    const cy = y + it.lay.h * 0.55;
    const did = `${id}-d${slug(it.lay.lines.join(' '))}`; // content, not position (TT-1150)
    B.shape({ id: `${did}-check`, kind: 'connector', d: `M ${fmt(cx)} ${fmt(cy)} L ${fmt(cx + 7)} ${fmt(cy + 7)} L ${fmt(cx + 17)} ${fmt(cy - 9)}`, stroke: palette.accent, fill: 'none', strokeWidth: 3, sent: it.sent, z: 2 });
    B.text(did, it.lay, r.x + pad + check, y, L.fs, fonts.caps, palette.ink, { sent: it.sent, uppercase: true });
    y += it.lay.h + STORY.decisionGap;
  });
}

/**
 * Lay out one tension: two wrapped cloud labels side by side (`stacked`:
 * one above the other — a much narrower block for a full board) at a
 * register `scale`. `reach` is how far the clouds' scallops paint outside
 * their rects; the reserved block is inflated by it so neighbours stay clear.
 */
function layoutTension(S, tn, { stacked = false, scale = 1 } = {}) {
  const fs = STORY.tension * scale;
  const a = wrap(up(tn.a), fs, STORY.tensionMaxW * scale, S.capsM);
  const b = wrap(up(tn.b), fs, STORY.tensionMaxW * scale, S.capsM);
  const cp = containerPadding('cloud', fs);
  const aw = a.w + cp * 2;
  const bw = b.w + cp * 2;
  const ch = Math.max(a.h, b.h) + cp * 1.6;
  const reach = Math.max(cloudReach({ w: aw, h: ch }), cloudReach({ w: bw, h: ch }));
  const gap = (stacked ? STORY.tensionGapStacked : STORY.tensionGap) * scale;
  return { a, b, cp, aw, bw, ch, fs, reach, gap, stacked };
}

/** The block a tension layout reserves when inflated by `bump` (reach or 0). */
function tensionBlock(L, bump) {
  return L.stacked ? { w: Math.max(L.aw, L.bw) + bump * 2, h: L.ch * 2 + L.gap + bump * 2 } : { w: L.aw + L.gap + L.bw + bump * 2, h: L.ch + bump * 2 };
}

/**
 * Try to place a tension along a `plan`: [where, bump] steps. `where` =
 * 'sky' (the template's spot, then across the sky strip — short,
 * zone-restricted searches), 'anywhere' (the template's spot as a free
 * search, then the bottom of the sheet) or 'hold' (only the previous
 * placement). `bump` = the clouds' reach, or 0 as a last resort (a full sky
 * and a tight bank, e.g. the river board): only the cloud boxes are
 * reserved and the scallops may kiss a neighbour, which beats losing the
 * tension. Returns { r, bump } or null.
 */
function tryTension(S, id, i, L, plan, pad = STORY.tensionPad) {
  const { T, sheet } = S;
  for (const [where, bump] of plan) {
    const { w, h } = tensionBlock(L, bump);
    const midY = T.tensionsAt[1] * sheet.h + i * (h + STORY.tensionRowGap);
    const base = [T.tensionsAt[0] * sheet.w, midY];
    let r = null;
    if (where === 'sky') r = S.placeInSky(id, w, h, [base, [sheet.w * 0.5, midY], [sheet.w * 0.75, midY], [sheet.w * 0.25, midY]], pad);
    else if (where === 'anywhere') r = S.place(id, w, h, base, { pad }) || S.place(id, w, h, [sheet.w * 0.5, sheet.h * 0.9], { pad });
    else r = S.place(id, w, h, base, { pad, holdOnly: true });
    if (r) return { r, bump };
  }
  return null;
}

/** Two clouds and the pull-apart arrow between them (horizontal or vertical). */
function drawTension(S, E, { tn, id }, L, r, bump) {
  const { palette, fonts } = S;
  const { a, b, cp, aw, bw, ch, fs, gap, stacked } = L;
  const rr = makeRng(seedFor(id, 19));
  const B = E.block(id);
  const cloud = (x, y, wdt, lay, key) => {
    B.shape({ id: `${id}-${key}`, kind: 'container', d: containerPath('cloud', { x, y, w: wdt, h: ch }, rr), stroke: palette.ink, fill: palette.paper, fillOpacity: 0.95, strokeWidth: 2.4, sent: tn.sent, z: 2 });
    B.text(`${id}-${key}-t`, lay, x + cp, y + cp * 0.8, fs, fonts.caps, palette.ink, { sent: tn.sent, uppercase: true });
  };
  const hs = STORY.tensionHead;
  const inset = STORY.tensionArrowInset;
  let d;
  if (stacked) {
    const x0 = r.x + bump;
    const y0 = r.y + bump;
    cloud(x0, y0, aw, a, 'a');
    cloud(x0, y0 + ch + gap, bw, b, 'b');
    const x = x0 + Math.max(aw, bw) / 2;
    const y1 = y0 + ch + inset;
    const y2 = y0 + ch + gap - inset;
    d = `M ${fmt(x)} ${fmt(y1)} L ${fmt(x)} ${fmt(y2)} M ${fmt(x - hs)} ${fmt(y1 + hs)} L ${fmt(x)} ${fmt(y1)} L ${fmt(x + hs)} ${fmt(y1 + hs)} M ${fmt(x - hs)} ${fmt(y2 - hs)} L ${fmt(x)} ${fmt(y2)} L ${fmt(x + hs)} ${fmt(y2 - hs)}`;
  } else {
    const y0 = r.y + bump;
    cloud(r.x + bump, y0, aw, a, 'a');
    cloud(r.x + bump + aw + gap, y0, bw, b, 'b');
    const y = y0 + ch / 2;
    const x1 = r.x + bump + aw + inset;
    const x2 = r.x + bump + aw + gap - inset;
    d = `M ${fmt(x1)} ${fmt(y)} L ${fmt(x2)} ${fmt(y)} M ${fmt(x1 + hs)} ${fmt(y - hs)} L ${fmt(x1)} ${fmt(y)} L ${fmt(x1 + hs)} ${fmt(y + hs)} M ${fmt(x2 - hs)} ${fmt(y - hs)} L ${fmt(x2)} ${fmt(y)} L ${fmt(x2 - hs)} ${fmt(y + hs)}`;
  }
  B.shape({ id: `${id}-arrow`, kind: 'connector', d, stroke: palette.secondary || palette.accent, fill: 'none', strokeWidth: 3, sent: tn.sent, z: 2 });
}

/**
 * Tensions: two clouds pulling apart, in the sky. `i` is the tension's row:
 * its index in the SPEC. Only the proper try — the sky, side by side, reach
 * reserved — runs here; a tension the sky cannot take that way is DEFERRED
 * (see placeLeftoverTensions): its relaxed tries would otherwise land on the
 * flanks or the bottom strip — exactly the themes' slots — and, running
 * before the themes, push a theme off the board. A held tension first asks
 * its old spot to absorb any shape (exact size first), so a re-solve never
 * flips a placed pair between layouts.
 */
function placeTensions(S, E, items) {
  items.forEach((it) => {
    const { id, i } = it;
    const layouts = STORY.tensionLadder.map((opt) => layoutTension(S, it.tn, opt));
    if (S.prev?.[id]) {
      const sizes = layouts.flatMap((L) => [L.reach, 0].map((bump) => ({ L, bump, ...tensionBlock(L, bump) })));
      const held = S.holdLadder(S.prev[id], sizes, (sz) => tryTension(S, id, i, sz.L, [['hold', sz.bump]])?.r);
      if (held) return drawTension(S, E, it, held.L.L, held.r, held.L.bump);
    }
    const L = layouts[0];
    const hit = tryTension(S, id, i, L, [['sky', L.reach]]);
    if (hit) drawTension(S, E, it, L, hit.r, hit.bump);
    else S.deferredTensions.push({ it, layouts });
  });
}

/**
 * Tensions the sky could not take get whatever room is left after the
 * themes: side by side with the scallops unreserved, then stacked, then a
 * compact stacked register — each in the sky first, then anywhere.
 */
function placeLeftoverTensions(S, E) {
  for (const { it, layouts } of S.deferredTensions) {
    for (const [k, L] of layouts.entries()) {
      const plan = k === 0 ? [['sky', 0], ['anywhere', L.reach], ['anywhere', 0]] : [['sky', L.reach], ['sky', 0], ['anywhere', L.reach], ['anywhere', 0]];
      const hit = tryTension(S, it.id, it.i, L, plan, STORY.tensionPadLeftover);
      if (hit) {
        drawTension(S, E, it, L, hit.r, hit.bump);
        break;
      }
    }
  }
}

/**
 * Theme slot → target centre for a block of (w, h): corners and bottom
 * strips are computed from the block size so a wide block is aimed inside
 * the sheet.
 */
function slotPoint(S, slot, w, h) {
  const { sheet, M, art } = S;
  const leftX = Math.max(M + w / 2 + 6, art.x * 0.5);
  const rightX = Math.min(sheet.w - M - w / 2 - 6, art.x + art.w + (sheet.w - art.x - art.w) * 0.5);
  const bottomY = sheet.h - M - h / 2 - 6;
  switch (slot) {
    case 'left-low':
      return [leftX, sheet.h * 0.72];
    case 'left-mid':
      return [leftX, sheet.h * 0.45];
    case 'right-low':
      return [rightX, sheet.h * 0.78];
    case 'right-mid':
      return [rightX, sheet.h * 0.5];
    case 'base-right':
      return [Math.min(sheet.w - M - w / 2, art.x + art.w * 0.72), bottomY];
    case 'base-left':
      return [Math.max(M + w / 2, art.x + art.w * 0.2), bottomY];
    case 'flank-right':
      return [art.x + art.w * 0.7, sheet.h * 0.58];
    case 'corner-right':
      return [sheet.w - M - w / 2 - 6, bottomY];
    case 'corner-left':
      return [M + w / 2 + 6, bottomY];
    case 'sky-mid':
      return [sheet.w * 0.5, M + h / 2 + 6];
    case 'sky-right':
      return [sheet.w - M - w / 2 - 6, sheet.h * 0.36];
    default:
      return [sheet.w * 0.5, sheet.h * 0.5];
  }
}

/**
 * Themes: the margins and the pale wash, in template slots. `i` is the
 * theme's place in stage order (its home slot must not depend on which
 * neighbours are held); it tries its own slot, then the next few.
 */
function placeThemes(S, E, items) {
  const { T, palette, fonts, measure } = S;
  const slots = T.themeSlots || STORY.themeSlots;
  const tries = Math.min(STORY.themeSlotTries, slots.length);
  items.forEach(({ cl, i }) => {
    // Jitter is seeded per theme, so one theme's tries never shift another's.
    const themeRng = makeRng(seedFor(cl.id, 37));
    const jitter = () => randRange(themeRng, -STORY.themeJitter, STORY.themeJitter);
    // The ladder as this theme sees it: a step that trims nothing the
    // theme has repeats an earlier rung, so each effective shape is laid
    // out (measured once — the hold pass and the fit loop share it) and
    // sought once.
    const rungs = [];
    const shapes = new Set();
    for (const opt of STORY.themeLadder) {
      const trimmed = trimCluster(cl, opt.keep);
      const shape = `${opt.maxW}|${trimmed.children.length}|${trimmed.quotes.length}`;
      if (!shapes.has(shape)) {
        shapes.add(shape);
        rungs.push({ opt, cl: trimmed });
      }
    }
    const laid = new Map();
    const layoutAt = (rung) => {
      if (!laid.has(rung)) {
        const L = layoutCluster(rung.cl, measure, fonts, rung.opt);
        L.parts = clusterParts(L);
        laid.set(rung, L);
      }
      return laid.get(rung);
    };
    const at = (L, s, holdOnly, jit) => {
      const p = slotPoint(S, slots[(i + s) % slots.length], L.w, L.h);
      return S.place(cl.id, L.w, L.h, jit ? [p[0] + jitter(), p[1] + jitter()] : p, { pad: STORY.themePad, parts: L.parts, holdOnly });
    };
    // Held: the old spot absorbs whichever layout it can before the theme moves.
    const prevRect = S.prev?.[cl.id];
    let { r, L: local } = (prevRect && S.holdLadder(prevRect, rungs.map(layoutAt), (L, holdOnly) => at(L, 0, holdOnly, false))) || {};
    for (const rung of rungs) {
      if (r) break;
      local = layoutAt(rung);
      for (let s = 0; !r && s < tries; s++) r = at(local, s, false, true);
    }
    if (!r) return;
    emitClusterItems(cl, local, r, { E, palette, fonts, clusterPad: WALL.clusterPad, arrowStroke: STORY.themeArrowStroke, arrowHead: STORY.themeArrowHead, paper: true });
  });
}

/** Camera focus: the placed idea / theme that arrived last (largest `sent`). */
function pickFocus(S) {
  let focus = null;
  const consider = (id, sent) => {
    const r = S.ctx.rects[id];
    if (!r) return;
    if (!focus || (sent ?? 0) >= focus.sent) focus = { id, sent: sent ?? 0, rect: r };
  };
  S.ideas.forEach((b) => consider(b.id, b.sent));
  S.themes.forEach((c) => consider(c.id, latestSent(c)));
  return focus;
}

// ------------------------------------------------------------------ solve

/**
 * @param spec  story spec
 * @param ctx   { sheet, margin, occ, anchor, assets, zones, rects, placed, skipped }
 * @param helpers { measure, placeBlock, commitSpot, holdLadder, palette, fonts, prev, T }
 * @returns { texts, shapes, icons, images, focus, beatPts }
 */
export function solveStory(spec, ctx, helpers) {
  const S = makeState(spec, ctx, helpers);
  const { prev } = S;

  // Content lists in their canonical order (stage order; spec order for
  // tensions). Ideas sharing a beat get a fan slot from this order.
  S.ideas = byStage(spec.big_ideas);
  const stageCount = {};
  S.fanOf = {};
  S.ideas.forEach((idea) => {
    const k = stageCount[idea.stage] || 0;
    stageCount[idea.stage] = k + 1;
    S.fanOf[idea.id] = STORY.fan[k] ?? k;
  });
  const decisions = (spec.decisions || []).filter((d) => d && d.text);
  // A tension is keyed by its own id, else by a hash of its two poles (never
  // its position: `specAtSentence` drops unheard tensions).
  const tensions = (spec.tensions || []).map((tn, i) => ({ tn, i, id: tn.id != null ? String(tn.id) : `tension-${seedFor(`${tn.a}|${tn.b}`).toString(36)}` }));
  S.themes = byStage(spec.themes);
  const themes = S.themes.map((cl, i) => ({ cl, i }));

  // Structural sections: fixed to the art.
  const structural = bucket();
  placeTitleBlock(S, structural);
  placeObjects(S, structural);
  drawRoute(S, structural);
  placeBeats(S, structural);
  reserveRoute(S, ctx);

  // Content sections: held items first (across ALL sections), then new.
  const buckets = Object.fromEntries(CONTENT_SECTIONS.map((k) => [k, bucket()]));
  const isHeld = (id) => !!prev?.[id];
  S.deferredTensions = [];
  for (const held of [true, false]) {
    placeIdeas(S, buckets.ideas, S.ideas.filter((it) => isHeld(it.id) === held));
    if (isHeld('decisions') === held) placeDecisions(S, buckets.decisions, decisions);
    placeTensions(S, buckets.tensions, tensions.filter((it) => isHeld(it.id) === held));
    placeThemes(S, buckets.themes, themes.filter((it) => isHeld(it.cl.id) === held));
  }
  // Tensions the sky could not take: after the themes, so they never cost one.
  placeLeftoverTensions(S, buckets.tensions);

  const all = [structural, ...CONTENT_SECTIONS.map((k) => buckets[k])];
  const cat = (key) => all.flatMap((E) => E[key]);
  return { texts: cat('texts'), shapes: cat('shapes'), icons: cat('icons'), images: cat('images'), focus: pickFocus(S), beatPts: S.beatPts };
}
